import { useRef } from 'react';
import { ArrowUp, Loader2 } from 'lucide-react';

export interface HelpInputProps {
  /** Used in the placeholder and the visually-hidden label. */
  contextLabel: string;
  value: string;
  onChange: (text: string) => void;
  /** Receives the trimmed text; the field clears. */
  onSubmit: (text: string) => void;
  /** Sending disables the field and swaps the icon for a spinner. */
  status?: 'idle' | 'sending';
}

const MAX_HEIGHT_PX = 72;
const INPUT_ID = 'dva-help-input';

/**
 * One field that grows to three lines, with the send button inside it at the right
 * edge. The button is coral with a navy glyph (4.4:1); white on coral would fail.
 */
export function HelpInput({ contextLabel, value, onChange, onSubmit, status = 'idle' }: HelpInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sending = status === 'sending';
  const canSend = !sending && value.trim().length > 0;

  const grow = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
  };

  const submit = () => {
    if (!canSend) return;
    onSubmit(value.trim());
    onChange('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  return (
    <div className="shrink-0 px-4 pb-3 xl:px-5">
      <label htmlFor={INPUT_ID} className="sr-only">
        {`Ask about ${contextLabel}`}
      </label>

      <div
        className={[
          'flex min-h-[52px] items-center gap-2 rounded-xl border border-[#e5e7eb] bg-white pl-3.5 pr-1 xl:min-h-[46px] xl:rounded-[10px] xl:pr-1.5',
          sending ? '' : 'focus-within:border-dvaNavy focus-within:shadow-[0_0_0_3px_rgba(255,110,76,0.35)]',
        ].join(' ')}
      >
        <textarea
          ref={textareaRef}
          id={INPUT_ID}
          rows={1}
          value={value}
          disabled={sending}
          placeholder={sending ? 'Waiting for a reply…' : `Ask about ${contextLabel}…`}
          onChange={(event) => {
            onChange(event.target.value);
            grow(event.target);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          className="max-h-[72px] w-full resize-none border-0 bg-transparent py-3 text-sm leading-[1.45] text-dvaNavy placeholder:text-[#717182] focus:outline-none xl:py-2.5 xl:text-[13.5px]"
        />

        <button
          type="button"
          onClick={submit}
          disabled={!canSend}
          aria-label={sending ? 'Sending' : 'Send'}
          className={[
            'flex size-11 shrink-0 items-center justify-center rounded-[9px] xl:size-[34px] xl:rounded-lg',
            'focus-visible:ring-[3px] focus-visible:ring-dvaOrange/35 focus-visible:outline-none',
            canSend ? 'bg-dvaOrange' : 'bg-dvaOrange/40',
          ].join(' ')}
        >
          {sending ? (
            <Loader2 className="size-[17px] animate-spin text-dvaNavy" strokeWidth={2.4} aria-hidden="true" />
          ) : (
            <ArrowUp className="size-[17px] text-dvaNavy" strokeWidth={2.4} aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}
